import { Chip } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { MdPeople } from '@teamworksdev/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RouterOutput } from '../../../trpc';
import { trpc } from '../../../trpc/react';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { DataTableWithContext } from '../../components/table/data-table';
import type {
  DataTableFilter,
  FilterDataType,
  FilterOption,
} from '../../components/table/data-table-context';
import { DataTablePlaceholder } from '../../components/table/data-table-placeholder';
import { UserPermissionsDrawer } from './UserPermissionsDrawer';

type PermissionsCoach =
  RouterOutput['permissions']['coaches']['coaches'][number];

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE, 50, 100];

/**
 * Team names come from Axle, except the org-level staff team, which is created
 * as the literal `coaches` and is translated for display (as in TeamSelect).
 */
const COACHES_TEAM_NAME = 'coaches';

const SEARCH_DEBOUNCE_MS = 300;

export function PermissionsPage() {
  const { t } = useTranslation();
  // `search` drives the input so typing stays responsive; `searchTerm` is the
  // debounced value the request is keyed on.
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);

  const coachesQuery = trpc.permissions.coaches.useQuery({
    search: searchTerm.trim() || undefined,
    teamIds,
    limit: pageSize,
    offset: pageIndex * pageSize,
  });
  const teamsQuery = trpc.permissions.teams.useQuery();

  const total = coachesQuery.data?.total ?? 0;
  const selectedCoach = coachesQuery.data?.coaches.find(
    (coach) => coach.profileId === selectedCoachId,
  );

  const teamLabel = useCallback(
    (name: string) => (name === COACHES_TEAM_NAME ? t('coaches') : name),
    [t],
  );

  const columnHelper = createColumnHelper<PermissionsCoach>();
  const columns = [
    columnHelper.accessor('name', {
      header: () => t('name'),
      cell: ({ row }) => row.original.name,
    }),
    columnHelper.accessor('teams', {
      header: () => t('teams'),
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-2">
          {row.original.teams.map((team) => (
            <Chip
              key={team.id}
              label={teamLabel(team.name)}
              size="small"
              sx={{
                height: 20,
                '.MuiChip-label': { px: 1, fontSize: 11, lineHeight: '14px' },
              }}
            />
          ))}
        </div>
      ),
    }),
  ];

  const teamsFilter: DataTableFilter<string> = {
    id: 'teams',
    title: t('teams'),
    type: 'select',
    options: (teamsQuery.data ?? []).map<FilterOption<string>>((team) => ({
      id: team.id,
      label: teamLabel(team.name),
      value: team.id,
    })),
    multiselect: true,
    showSearch: true,
    showSelectAll: true,
  };

  // Paging resets with the debounced term rather than on keystroke, so the
  // request input changes exactly once per settled search.
  const applySearch = useCallback((value: string) => {
    setSearchTerm(value);
    setPageIndex(0);
  }, []);

  const debouncedApplySearch = useMemo(
    () => debounce(applySearch, SEARCH_DEBOUNCE_MS),
    [applySearch],
  );

  useEffect(() => () => debouncedApplySearch.clear(), [debouncedApplySearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debouncedApplySearch(value);
    },
    [debouncedApplySearch],
  );

  const handleFilterChange = useCallback(
    (filters: Record<string, FilterDataType[]>) => {
      setTeamIds((filters['teams'] as string[] | undefined) ?? []);
      setPageIndex(0);
    },
    [],
  );

  return (
    <Page>
      <PageHeader title={t('permissions')} />
      <main className="flex grow flex-col overflow-hidden p-6">
        <DataTableWithContext
          title={t('usersCount', { count: total })}
          columns={columns}
          data={coachesQuery.data?.coaches ?? []}
          loading={coachesQuery.isLoading}
          showError={coachesQuery.isError}
          enableSearch
          searchValue={search}
          searchPlaceholder={t('searchUsersByName')}
          onSearchChange={handleSearchChange}
          enableFilters
          filters={[teamsFilter]}
          onFilterChange={handleFilterChange}
          enablePagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={total}
          onPaginationChange={(pagination) => {
            setPageIndex(pagination.pageIndex);
            setPageSize(pagination.pageSize);
          }}
          getRowId={(coach) => coach.profileId}
          onRowClick={(coach) => setSelectedCoachId(coach.profileId)}
          NoDataPlaceholder={() => (
            <DataTablePlaceholder
              icon={<MdPeople size={24} />}
              title={t('noUsersFound')}
            />
          )}
        />
      </main>

      <UserPermissionsDrawer
        profileId={selectedCoachId}
        coachName={selectedCoach?.name}
        open={!!selectedCoachId}
        onClose={() => setSelectedCoachId(null)}
        onSaved={() => coachesQuery.refetch()}
      />
    </Page>
  );
}
